import type { PresentationPreview } from 'paintscout'

/**
 * Fixed canvas widths per device-preview mode (design mock's `WIDTHS`). A single source of truth
 * shared by the editor canvas column and the preview iframe — kept separate before this, the two
 * disagreed (391/799 vs. these numbers) without either side knowing.
 */
export const PRESENTATION_DEVICE_WIDTH: Record<PresentationPreview, number> = {
  none: 980,
  mobile: 390,
  tablet: 834,
  desktop: 1180
}

/** `preview` is `undefined` in edit mode, not `'none'` — this normalizes the lookup. */
export function getPresentationDeviceWidth(preview?: PresentationPreview): number {
  return PRESENTATION_DEVICE_WIDTH[preview || 'none']
}

/** One side of `PresentationImbedPreview`'s `deviceFrame` border. */
export const PRESENTATION_DEVICE_FRAME_BORDER = 8

/**
 * Width the framed preview occupies on screen: the canvas width plus both frame borders. Chrome
 * placed beside or above the frame (e.g. the layout preview's header row) has to match this number,
 * not the canvas width, or its edges sit inside the frame's edges.
 */
export function getPresentationFrameWidth(preview?: PresentationPreview): number {
  return getPresentationDeviceWidth(preview) + PRESENTATION_DEVICE_FRAME_BORDER * 2
}

/**
 * Display name per device-preview mode — "iPad" for `tablet`, matching the design mock's copy.
 * Single source so the device float's tooltip and the preview banner's label can't drift apart.
 */
export const PRESENTATION_DEVICE_LABEL: Record<'mobile' | 'tablet' | 'desktop', string> = {
  mobile: 'Mobile',
  tablet: 'iPad',
  desktop: 'Desktop'
}

const PREVIEW_BANNER_MESSAGE = 'This is what your customer sees. Editing is off.'

/**
 * Second line of the builder's preview banner, e.g. "iPad · This is what your customer sees.
 * Editing is off." — the device prefix is dropped in edit mode (`'none'` or `undefined`).
 */
export function getPreviewBannerSubtitle(preview?: PresentationPreview): string {
  if (!preview || preview === 'none') {
    return PREVIEW_BANNER_MESSAGE
  }

  return `${PRESENTATION_DEVICE_LABEL[preview]} · ${PREVIEW_BANNER_MESSAGE}`
}
